import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIsCompact } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';

/**
 * The filter row every list page uses.
 *
 * Above `lg` it is a grid of labelled controls. Below `lg` it collapses to one
 * line — the page's own search box, plus a Filters button badged with how many
 * filters are actually narrowing anything — and the controls move into a sheet
 * that slides up from the bottom.
 *
 * Two things make that safe to collapse: the summary line stays on the page, so
 * the filter state is still stated in words when no control is visible; and the
 * badge counts only fields that differ from their reset value, so "Filters"
 * with no badge genuinely means "you are seeing everything".
 *
 * The sheet stages its changes and commits them on Apply rather than firing per
 * control. On a desktop a stray refetch costs nothing; on a phone, six selects
 * meant six round trips and six spinners before the reader saw the answer they
 * were assembling.
 *
 * `busy` locks the whole bar — the page's search box included — while the
 * request it drives is in flight. Changing a filter mid-request would either
 * race the answer or queue a second one behind it, and neither is visible to
 * someone who just sees the old rows sitting there.
 */

export interface FilterField {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Present for a select; omit for a date field. */
  options?: { value: string; label: string }[];
  type?: 'select' | 'date';
  /**
   * Renders a control the two built-in types cannot express — a multi-select,
   * say. It is handed the staged value rather than the committed one, so a
   * custom control takes part in the mobile sheet's draft like any other
   * field. Encode whatever it needs as the string.
   */
  render?: (control: {
    id: string;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
  }) => ReactNode;
  /**
   * The value that means "not filtering". Defaults to the first option's
   * value, which is the "Any" entry by convention.
   */
  resetValue?: string;
  disabled?: boolean;
  /**
   * Options are alphabetised by default — a caller's array order is an accident
   * of how the data arrived. Set false where the order carries meaning, such as
   * a lifecycle or a scale.
   */
  sort?: boolean;
}

function sortedOptions(field: FilterField, language: string) {
  const options = field.options ?? [];
  // The first entry is the reset ("Any") and keeps its place at the top rather
  // than sorting to wherever its translation happens to land.
  if (field.sort === false || options.length < 3) return options;
  const [reset, ...rest] = options;
  return [
    reset,
    ...[...rest].sort((a, b) =>
      a.label.localeCompare(b.label, language, { sensitivity: 'base' })
    ),
  ];
}

function resetValueOf(field: FilterField) {
  return field.resetValue ?? field.options?.[0]?.value ?? '';
}

function Control({
  field,
  value,
  onChange,
  language,
  busy,
}: {
  field: FilterField;
  value: string;
  onChange: (value: string) => void;
  language: string;
  busy?: boolean;
}) {
  const disabled = busy || field.disabled;
  const id = `filter-${field.id}`;

  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-muted-foreground text-xs">
        {field.label}
      </Label>
      {field.render ? (
        field.render({ id, value, onChange, disabled })
      ) : field.type === 'date' ? (
        <Input
          id={id}
          type="date"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger id={id} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sortedOptions(field, language).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

/** Static class names, so Tailwind can see every one of them. */
const DESKTOP_COLUMNS = {
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
} as const;

export function FilterBar({
  fields,
  search,
  summary,
  className,
  busy,
  columns = 4,
}: {
  fields: FilterField[];
  /** The page's own search form; kept beside the Filters button on a phone. */
  search?: ReactNode;
  summary?: ReactNode;
  className?: string;
  /** True while the request these filters drive is in flight. */
  busy?: boolean;
  /**
   * Controls per row on a desktop. Raise it when a row of four would strand a
   * field on a line of its own — a "To" date apart from its "From".
   */
  columns?: keyof typeof DESKTOP_COLUMNS;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const isCompact = useIsCompact();

  const [open, setOpen] = useState(false);
  /** The sheet edits a copy; committed state stays put until Apply. */
  const [draft, setDraft] = useState<Record<string, string>>({});

  // Re-seed whenever the sheet opens, so a dismissed edit is discarded.
  useEffect(() => {
    if (open) {
      setDraft(
        Object.fromEntries(fields.map((field) => [field.id, field.value]))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeCount = fields.filter(
    (field) => field.value !== resetValueOf(field)
  ).length;

  function apply() {
    // Only fire the fields whose value actually moved; each one typically
    // triggers a fetch in the parent.
    fields.forEach((field) => {
      const next = draft[field.id];
      if (next !== undefined && next !== field.value) field.onChange(next);
    });
    setOpen(false);
  }

  function reset() {
    // Resets the draft rather than committing it — a mis-tap on Reset is not a
    // lost filter set plus a refetch.
    setDraft(
      Object.fromEntries(fields.map((field) => [field.id, resetValueOf(field)]))
    );
  }

  if (!isCompact) {
    return (
      <div className={cn('space-y-3', className)}>
        {search && (
          <fieldset disabled={busy} className="contents">
            {search}
          </fieldset>
        )}
        <div className={cn('grid gap-3 sm:grid-cols-2', DESKTOP_COLUMNS[columns])}>
          {fields.map((field) => (
            <Control
              key={field.id}
              field={field}
              value={field.value}
              onChange={field.onChange}
              language={language}
              busy={busy}
            />
          ))}
        </div>
        {summary}
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2">
        {search && (
          <fieldset disabled={busy} className="min-w-0 flex-1">
            {search}
          </fieldset>
        )}
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => setOpen(true)}
          className={cn('h-10 shrink-0', !search && 'w-full')}
        >
          <SlidersHorizontal className="size-4" />
          {t('filters.title')}
          {activeCount > 0 && (
            <Badge className="ml-0.5 tabular-nums">{activeCount}</Badge>
          )}
        </Button>
      </div>
      {summary}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[85svh] gap-0 overflow-y-auto rounded-t-xl p-0"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle>{t('filters.title')}</SheetTitle>
          </SheetHeader>

          <div className="space-y-3 p-4">
            {fields.map((field) => (
              <Control
                key={field.id}
                field={field}
                value={draft[field.id] ?? field.value}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, [field.id]: value }))
                }
                language={language}
                busy={busy}
              />
            ))}
          </div>

          {/* Sticky so Apply is reachable without scrolling a long filter set
              back to the bottom. */}
          <div className="bg-background sticky bottom-0 flex gap-2 border-t p-4">
            <Button
              variant="outline"
              className="flex-1"
              disabled={busy}
              onClick={reset}
            >
              {t('filters.reset')}
            </Button>
            <Button className="flex-1" disabled={busy} onClick={apply}>
              {t('filters.apply')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
